"use client";

import { Box, Button, Container, Grid, TextField, Typography, type SxProps, type Theme } from "@mui/material";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import contactService from "@/app/_services/contactService";
import { PrivateEventsLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TheaterComedyOutlinedIcon from "@mui/icons-material/TheaterComedyOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ScrollReveal from "@/app/components/ScrollReveal";
import posthog from "posthog-js";

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

type PrivateEventLogo = {
  src: string;
  alt: string;
  size?: "default" | "large" | "xlarge" | "xxlarge";
  nudgeDown?: boolean;
  /** Multiplier on base height/maxWidth from `size` (e.g. 0.5 = half, 2 = double). */
  scale?: number;
};

const PRIVATE_EVENT_LOGOS: PrivateEventLogo[] = [
  {
    src: "https://storage.googleapis.com/cuo-website/Akin+Gump+Logo.jpg",
    alt: "Akin",
    size: "xlarge",
    scale: 0.5,
  },
  {
    src: "https://storage.googleapis.com/cuo-website/JP+Morgan+Logo+(1).png",
    alt: "J.P. Morgan",
    size: "xxlarge",
    scale: 2,
  },
  { src: "/images/private-events-YPO_Logo.png", alt: "YPO", size: "large" },
  { src: "/images/private-events-Norton_Rose_Fulbright_Logo.png", alt: "Norton Rose Fulbright", size: "xxlarge", nudgeDown: true },
  { src: "/images/private-events-Skadden_Arps_Logo.png", alt: "Skadden", size: "large" },
  { src: "/images/private-events-Sydecar.png", alt: "Sydecar", size: "xlarge" },
  { src: "/images/private-events-Proskauer_Rose_Logo.png", alt: "Proskauer" },
  { src: "/images/private-events-Moritt_Hock_Hamroff_LLP.png", alt: "Moritt Hock & Hamroff LLP", size: "xxlarge" },
  { src: "/images/private-events-Polymarket.png", alt: "Polymarket" },
  { src: "/images/private-events-BILT_Rewards_Logo.png", alt: "BILT Rewards" },
  { src: "/images/private-events-Alison_Brod_Marketing_Only_Women_Staff.png", alt: "Alison Brod Marketing", size: "xlarge" },
];

function privateEventLogoDims(size?: PrivateEventLogo["size"], scale = 1) {
  const base =
    size === "xxlarge"
      ? { height: 110, maxWidth: 480 }
      : size === "xlarge"
        ? { height: 92, maxWidth: 400 }
        : size === "large"
          ? { height: 44, maxWidth: 180 }
          : { height: 32, maxWidth: 140 };
  return {
    height: Math.max(8, Math.round(base.height * scale)),
    maxWidth: Math.max(16, Math.round(base.maxWidth * scale)),
  };
}

const CHARCOAL_BG = "#0d0d0f";

const MARCELLO_BG_URL =
  "https://storage.googleapis.com/cuo-website/Marcello+%40+Paragon.jpg";

const gridCardRevealStyle: CSSProperties = { width: "100%", display: "flex" };

const gridCardItemSx = { display: "flex" } as const;

const gridCardSx = {
  width: "100%",
  flex: 1,
  display: "flex",
  flexDirection: "column",
} as const;

type FormatCard = {
  title: string;
  copy: string;
  imageSrc: string;
  imageAlt: string;
};

type TimelineStep = {
  time: string;
  title: string;
  details: string[];
};

type TimelineCard = {
  label: string;
  steps: TimelineStep[];
};

type AddOnCard = {
  title: string;
  copy: string;
  icon: ReactNode;
};

const WAYS_TO_WORK: FormatCard[] = [
  {
    title: "Internal Events",
    copy: "A global bank wanted a comedy show for summer associates and partners at one of our favorite venues. They gave us a 'no-no' list of topics, and our comedians delivered a clean performance.",
    imageSrc: "https://storage.googleapis.com/cuo-website/Bank%20Private%20Event.jpg",
    imageAlt: "Private comedy show for employees at a bank event",
  },
  {
    title: "Member Events",
    copy: "A prominent fintech company wanted a way to treat their loyalty members, so we produced a monthly comedy series inside the nicest restaurants in town.",
    imageSrc: "/images/st-theos.png",
    imageAlt: "Member comedy night with candlelit tables at a New York restaurant",
  },
  {
    title: "Client Events",
    copy: "A Big Law firm based in midtown wanted an immersive magic experience for their clients at a speakeasy, so we delivered four world-class mentalists and a stellar setting.",
    imageSrc: "https://storage.googleapis.com/cuo-website/Dim%20Sum%20Magic%20Show.jpg",
    imageAlt: "Close-up magic performed for clients at a private dinner",
  },
];

const FROM_YOU_ITEMS = ["Headcount", "Budget", "Desired location", "Comedy, magic, or both"] as const;

const WE_HANDLE_ITEMS = [
  "Venue sourcing and booking",
  "Talent sourcing and booking",
  "Upscale food and beverage service",
  "Audio / visual production",
  "Equipment rental, setup & breakdown",
  "Security and staffing",
  "Photography and media",
  "All communication and logistics",
] as const;

const RUN_OF_SHOW: TimelineCard[] = [
  {
    label: "Comedy",
    steps: [
      {
        time: "7:00",
        title: "Cocktail hour",
        details: ["Light bites and drinks served", "Guests mingle and find seats"],
      },
      {
        time: "7:45",
        title: "The show",
        details: ["Guests are seated", "Bar remains open", "4 comics and 1 emcee perform"],
      },
      {
        time: "9:00",
        title: "Wind down or double down",
        details: ["Drinks served", "Guests mingle and trickle out", "Flexibility to extend the night"],
      },
    ],
  },
  {
    label: "Magic",
    steps: [
      {
        time: "7:00",
        title: "Cocktail hour",
        details: ["Light bites and drinks", "Guests mingle and find seats"],
      },
      {
        time: "7:45",
        title: "The show",
        details: ["Guests are seated", "Bar remains open", "3 magicians perform on stage"],
      },
      {
        time: "8:30",
        title: "Close-up crowd magic",
        details: ["Guests mingle and explore", "Magicians perform for small groups"],
      },
      {
        time: "8:55",
        title: "The grand finale",
        details: ["Everyone gathers at the center", "Guests join one big final trick"],
      },
    ],
  },
];

const OPTIONAL_ADD_ONS: AddOnCard[] = [
  {
    title: "Dining",
    copy: "Add a dining element to elevate the night.",
    icon: <RestaurantOutlinedIcon sx={{ fontSize: 28 }} />,
  },
  {
    title: "Photographer",
    copy: "We capture the best moments of the night.",
    icon: <PhotoCameraOutlinedIcon sx={{ fontSize: 28 }} />,
  },
  {
    title: "Extended hours",
    copy: "Music stays on, bar stays open.",
    icon: <AccessTimeOutlinedIcon sx={{ fontSize: 28 }} />,
  },
];

const HERO_BADGE = "Private Events by ComedyUO";

const HERO_COPY = {
  headingLead: "We are in the",
  headingEmphasis: "hospitality business.",
  bodyPrefix:
    "Our specialty is providing high-touch experiences across both comedy and magic that offer opportunities for guests to socialize and mingle before the show and afterwards. As a production company, we can handle everything from the talent on stage to the ",
  bodyEmphasis: "toppings on your crostini.",
  cta: "Plan my comedy or magic event",
} as const;

const FROM_YOU_SECTION_ID = "from-you";
const INQUIRY_SECTION_ID = "private-event-inquiry";

const RUN_OF_SHOW_INTRO =
  "A typical night looks like this, and every block is flexible. Start earlier, run later, add dinner service, or keep it to a tight ninety minutes. Close-up magicians, mentalists, stand-up lineups, and hosted variety shows are all on the table.";

const HR_SECTION = {
  eyebrow: "Corporate-safe by default",
  heading: "HR can rest easy.",
  body: "Corporate lineups are built around observational humor: work, travel, family, technology and the absurdity of everyday life. Think the style of Jerry Seinfeld, Nate Bargatze or Jim Gaffigan.",
  disclaimer: "Style references only. Not proposed or guaranteed talent.",
  callout: "Every set is briefed in advance against your list of off-limit topics.",
} as const;

const NO_GO_TOPICS = [
  "Race and ethnicity",
  "Explicit sexual content and vulgarity",
  "Suicide and self harm",
  "LGBTQ+ identity or orientation",
  "Harassment and discrimination",
  "Disability",
  "Religion",
  "Politics and political ideology",
  "Sexual assault, abuse and violence",
] as const;

const SEO_FOOTNOTE =
  "ComedyUO produces private comedy and magic events across New York City for corporate teams, client entertainment, member clubs, and special occasions. We handle talent booking, venue sourcing, production, and show flow so you get a turnkey live experience.";

const heroEyebrowSx = {
  fontFamily: pageFont,
  fontSize: { xs: "0.6875rem", sm: "0.75rem" },
  fontWeight: 600,
  letterSpacing: { xs: "0.08em", sm: "0.16em" },
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.88)",
  lineHeight: 1.2,
} as const;

const sectionEyebrowSx = {
  fontFamily: pageFont,
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  color: "rgba(255,255,255,0.45)",
  textTransform: "uppercase",
  mb: 1,
} as const;

const sectionHeadingSx = {
  fontFamily: pageFont,
  fontWeight: 700,
  fontSize: { xs: "1.75rem", sm: "2.125rem", md: "2.5rem" },
  letterSpacing: "-0.02em",
  color: "#fff",
  mb: { xs: 2, md: 2.5 },
} as const;

const marcelloPanelSx = {
  backgroundColor: "rgba(13, 13, 15, 0.28)",
  backdropFilter: "blur(28px) saturate(1.12)",
  WebkitBackdropFilter: "blur(28px) saturate(1.12)",
  borderRadius: "14px",
  border: "1px solid rgba(255,255,255,0.16)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 28px 70px rgba(0,0,0,0.3)",
} as const;

const panelIconSx = {
  width: 44,
  height: 44,
  borderRadius: "8px",
  border: "1px solid rgba(255,255,255,0.16)",
  bgcolor: "rgba(255,255,255,0.06)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "rgba(255,255,255,0.85)",
} as const;

const numberBadgeSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: "8px",
  border: "1px solid rgba(255,255,255,0.16)",
  bgcolor: "rgba(255,255,255,0.06)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

type PageSectionProps = {
  id?: string;
  ariaLabelledBy?: string;
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  children: ReactNode;
};

function PageSection({ id, ariaLabelledBy, ariaLabel, sx, children }: PageSectionProps) {
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      sx={{
        py: { xs: 3.5, md: 5 },
        px: {
          xs: "max(16px, env(safe-area-inset-left, 0px))",
          md: 4,
        },
        pr: {
          xs: "max(16px, env(safe-area-inset-right, 0px))",
          md: 4,
        },
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
        fontFamily: pageFont,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(0, 0, 0, 0.48)",
    borderRadius: "8px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.28)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.42)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.52)", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    padding: "12px 14px",
    fontSize: "16px",
  },
  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
} as const;

export default function PrivateEventsPage() {
  const [reduceMotion, setReduceMotion] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [displayform, setDisplayform] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    posthog.capture("form_opened", { form_type: "private_event" });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = {
      name,
      email,
      phone_number: phone,
      comment,
      form_type: "contact_us",
    };
    try {
      const response = await contactService.contactUs(formData);
      const data = await response.json();
      setSuccessMessage(data.message);
      if (data) {
        setName("");
        setEmail("");
        setPhone("");
        setComment("");
        setDisplayform(true);
        posthog.capture("form_submitted", { form_type: "private_event" });
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
    }
  };

  useEffect(() => {
    if (typeof globalThis === "undefined") return;
    const win = globalThis as typeof globalThis & Window;
    const mql = win.matchMedia("(prefers-reduced-motion: reduce)");
    const syncReduce = () => setReduceMotion(mql.matches);
    syncReduce();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", syncReduce);
    } else {
      mql.addListener(syncReduce);
    }
    return () => {
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", syncReduce);
      } else {
        mql.removeListener(syncReduce);
      }
    };
  }, []);

  const scrollToInquiry = () => {
    const target = document.getElementById(INQUIRY_SECTION_ID);
    if (!target) return;
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  return (
    <>
      <PrivateEventsLayoutHelmet />
      <Header />
      <Box component="main">
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
          backgroundColor: CHARCOAL_BG,
          // Desktop keeps fixed cover. Mobile uses a viewport-height layer below so
          // cover isn't scaled to the full page height (which zooms Marcello in).
          backgroundImage: { xs: "none", md: `url('${MARCELLO_BG_URL}')` },
          backgroundSize: { md: "cover" },
          backgroundPosition: { md: "50% 36%" },
          backgroundAttachment: { md: "fixed" },
          backgroundRepeat: "no-repeat",
          fontFamily: pageFont,
        }}
      >
        <Box
          aria-hidden
          sx={{
            display: { xs: "block", md: "none" },
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "min(135svh, 980px)",
            zIndex: 0,
            backgroundImage: `url('${MARCELLO_BG_URL}')`,
            backgroundSize: "cover",
            backgroundPosition: "center 22%",
            backgroundRepeat: "no-repeat",
            pointerEvents: "none",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background: {
              xs: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.58) 40%, rgba(13,13,15,0.85) 85%, rgba(13,13,15,0.95) 100%), linear-gradient(90deg, rgba(20,12,8,0.35) 0%, transparent 55%)",
              md: "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 48%, rgba(0,0,0,0.35) 100%), linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.25) 40%, rgba(13,13,15,0.55) 80%, rgba(13,13,15,0.92) 100%)",
            },
            pointerEvents: "none",
          }}
        />

        <Box
          component="section"
          aria-label="Private events by ComedyUO"
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            minHeight: { xs: "auto", md: "min(92vh, 860px)" },
            display: "flex",
            alignItems: "center",
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              width: "100%",
              pl: { xs: "max(16px, env(safe-area-inset-left, 0px))", sm: 3, md: 4 },
              pr: { xs: "max(16px, env(safe-area-inset-right, 0px))", sm: 3, md: 4 },
              pt: { xs: 7, md: 10 },
              pb: { xs: 5, md: 10 },
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ maxWidth: 720 }}>
              <ScrollReveal variant="fadeUp">
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    maxWidth: "100%",
                    gap: 1,
                    px: 1.5,
                    py: 0.7,
                    mb: { xs: 2, md: 2.5 },
                    borderRadius: "999px",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backgroundColor: "rgba(12,12,14,0.55)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  }}
                >
                  <TheaterComedyOutlinedIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }} />
                  <Typography component="span" sx={heroEyebrowSx}>
                    {HERO_BADGE}
                  </Typography>
                </Box>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: pageFont,
                    fontWeight: 700,
                    fontSize: {
                      xs: "clamp(2.1rem, 8.4vw, 2.75rem)",
                      md: "clamp(3.5rem, 5.5vw, 4.75rem)",
                    },
                    lineHeight: { xs: 1.08, md: 1.02 },
                    letterSpacing: "-0.03em",
                    color: "#fff",
                    mb: { xs: 2, md: 2.25 },
                    maxWidth: 720,
                    overflowWrap: "break-word",
                  }}
                >
                  {HERO_COPY.headingLead}{" "}
                  <Box component="span" sx={{ whiteSpace: { xs: "normal", md: "nowrap" } }}>
                    {HERO_COPY.headingEmphasis}
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontFamily: pageFont,
                    fontSize: { xs: "0.975rem", sm: "1.05rem", md: "1.125rem" },
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: { xs: "100%", sm: 540 },
                    mb: { xs: 3, md: 3.5 },
                  }}
                >
                  {HERO_COPY.bodyPrefix}
                  <Box component="span" sx={{ whiteSpace: { xs: "normal", md: "nowrap" } }}>
                    {HERO_COPY.bodyEmphasis}
                  </Box>
                </Typography>

                <Button
                  onClick={scrollToInquiry}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  disableElevation
                  sx={{
                    fontFamily: pageFont,
                    fontWeight: 700,
                    fontSize: { xs: "0.9375rem", sm: "1rem" },
                    textTransform: "none",
                    borderRadius: "14px",
                    px: { xs: 2.75, sm: 3.25 },
                    py: { xs: 1.35, sm: 1.45 },
                    minHeight: { xs: 48, sm: 52 },
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: "#f2f2f2",
                    color: CHARCOAL_BG,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#fff", boxShadow: "none" },
                    "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 3 },
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }}
                >
                  {HERO_COPY.cta}
                </Button>
              </ScrollReveal>
            </Box>
          </Container>
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, overflow: "visible" }}>
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: { xs: -120, md: -160 },
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              background: {
                xs: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.08) 5%, rgba(0,0,0,0.32) 11%, rgba(0,0,0,0.46) 16%, rgba(0,0,0,0.54) 24%, rgba(0,0,0,0.58) 100%)",
                md: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 5%, rgba(0,0,0,0.34) 10%, rgba(0,0,0,0.48) 14%, rgba(0,0,0,0.56) 20%, rgba(0,0,0,0.62) 100%)",
              },
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1 }}>
      <PageSection ariaLabelledBy="ways-to-work-heading" sx={{ pt: { xs: 2.5, md: 3 } }}>
        <Container maxWidth="lg" sx={{ width: "100%", boxSizing: "border-box" }}>
          <ScrollReveal variant="fadeUp">
            <Typography id="ways-to-work-heading" component="h2" sx={{ ...sectionHeadingSx, mb: { xs: 2, md: 2.5 } }}>
              Ways to work with us
            </Typography>
          </ScrollReveal>
          <Grid container spacing={3} alignItems="stretch">
            {WAYS_TO_WORK.map((card, i) => (
              <Grid item xs={12} md={4} key={card.title} sx={gridCardItemSx}>
                <ScrollReveal variant={i % 2 === 0 ? "fadeLeft" : "fadeRight"} delay={i * 0.1} style={gridCardRevealStyle}>
                  <Box
                    sx={{
                      ...gridCardSx,
                      ...marcelloPanelSx,
                      p: { xs: 1.5, md: 1.75 },
                    }}
                  >
                    <Box
                      component="img"
                      src={card.imageSrc}
                      alt={card.imageAlt}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: { xs: 180, md: 200 },
                        objectFit: "cover",
                        display: "block",
                        borderRadius: "8px",
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        pt: { xs: 2, md: 2.25 },
                        px: { xs: 0.5, md: 0.75 },
                        minHeight: { md: 168 },
                      }}
                    >
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: pageFont,
                          fontWeight: 700,
                          fontSize: { xs: "1.125rem", md: "1.25rem" },
                          color: "#fff",
                          mb: 1.25,
                          lineHeight: 1.35,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: pageFont,
                          fontSize: "0.9375rem",
                          lineHeight: 1.65,
                          color: "rgba(255,255,255,0.72)",
                        }}
                      >
                        {card.copy}
                      </Typography>
                    </Box>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageSection>

      <PageSection id={FROM_YOU_SECTION_ID} ariaLabelledBy="from-you-heading" sx={{ scrollMarginTop: { xs: 80, md: 96 } }}>
        <Container maxWidth="lg" sx={{ width: "100%", boxSizing: "border-box" }}>
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
            <Grid item xs={12} md={5} sx={gridCardItemSx}>
              <ScrollReveal variant="fadeLeft" style={gridCardRevealStyle}>
                <Box sx={{ ...gridCardSx, ...marcelloPanelSx, p: { xs: 2.5, md: 3.5 } }}>
                  <Typography sx={sectionEyebrowSx}>From you</Typography>
                  <Typography id="from-you-heading" component="h2" sx={{ ...sectionHeadingSx, mb: { xs: 2.5, md: 3 } }}>
                    All we need is:
                  </Typography>
                  <Box component="ol" aria-label="Information we need from you" sx={{ listStyle: "none", m: 0, p: 0 }}>
                    {FROM_YOU_ITEMS.map((item, i) => (
                      <Box
                        component="li"
                        key={item}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.75,
                          mb: i < FROM_YOU_ITEMS.length - 1 ? 2.25 : 0,
                        }}
                      >
                        <Box aria-hidden sx={numberBadgeSx}>
                          <Typography
                            sx={{
                              fontFamily: pageFont,
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              color: "#fff",
                              lineHeight: 1,
                            }}
                          >
                            {i + 1}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: pageFont,
                            fontSize: { xs: "0.875rem", md: "1rem" },
                            fontWeight: 700,
                            letterSpacing: { xs: "0.02em", md: "0.06em" },
                            lineHeight: 1.4,
                            color: "#fff",
                            textTransform: "uppercase",
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </ScrollReveal>
            </Grid>
            <Grid item xs={12} md={7} sx={gridCardItemSx}>
              <ScrollReveal variant="fadeRight" delay={0.12} style={gridCardRevealStyle}>
                <Box sx={{ ...gridCardSx, ...marcelloPanelSx, p: { xs: 2.5, md: 3.5 } }}>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: pageFont,
                      fontWeight: 700,
                      fontSize: { xs: "1.125rem", md: "1.3125rem" },
                      color: "#fff",
                      mb: { xs: 2, md: 2.5 },
                      lineHeight: 1.35,
                    }}
                  >
                    And we&apos;ll handle the rest:
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {WE_HANDLE_ITEMS.map((item) => (
                      <Grid item xs={12} sm={6} key={item}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minHeight: 44 }}>
                          <CheckIcon sx={{ color: "rgba(255,255,255,0.55)", fontSize: 18, mt: 0.25, flexShrink: 0 }} />
                          <Typography
                            sx={{
                              fontFamily: pageFont,
                              fontSize: "0.875rem",
                              lineHeight: 1.45,
                              color: "rgba(255,255,255,0.82)",
                            }}
                          >
                            {item}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </ScrollReveal>
            </Grid>
          </Grid>
        </Container>
      </PageSection>

      <PageSection ariaLabelledBy="run-of-show-heading">
        <Container maxWidth="lg" sx={{ width: "100%", boxSizing: "border-box" }}>
          <ScrollReveal variant="fadeUp">
            <Typography sx={sectionEyebrowSx}>Run of show</Typography>
            <Typography id="run-of-show-heading" component="h2" sx={sectionHeadingSx}>
              À la carte, start to finish
            </Typography>
            <Typography
              sx={{
                fontFamily: pageFont,
                fontSize: "1rem",
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.78)",
                maxWidth: 720,
                mb: { xs: 3, md: 4 },
              }}
            >
              {RUN_OF_SHOW_INTRO}
            </Typography>
          </ScrollReveal>
          <Grid container spacing={3} alignItems="stretch">
            {RUN_OF_SHOW.map((card, i) => (
              <Grid item xs={12} md={6} key={card.label} sx={gridCardItemSx}>
                <ScrollReveal variant={i === 0 ? "fadeLeft" : "fadeRight"} delay={i * 0.1} style={gridCardRevealStyle}>
                  <Box sx={{ ...gridCardSx, ...marcelloPanelSx, p: { xs: 2.5, md: 3 }, minHeight: { md: 420 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                      <Box sx={{ ...panelIconSx, width: 40, height: 40 }}>
                        {card.label === "Comedy" ? (
                          <TheaterComedyOutlinedIcon sx={{ fontSize: 22, color: "#fff" }} />
                        ) : (
                          <AutoAwesomeOutlinedIcon sx={{ fontSize: 22, color: "#fff" }} />
                        )}
                      </Box>
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: pageFont,
                          fontWeight: 700,
                          fontSize: "1.125rem",
                          color: "#fff",
                        }}
                      >
                        {card.label}
                      </Typography>
                    </Box>
                    <Box component="ol" aria-label={`${card.label} event timeline`} sx={{ listStyle: "none", m: 0, p: 0, flex: 1 }}>
                      {card.steps.map((step, stepIdx) => (
                        <Box
                          component="li"
                          key={`${card.label}-${step.time}-${step.title}`}
                          sx={{
                            pb: stepIdx < card.steps.length - 1 ? 2 : 0,
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              pl: 2.25,
                              borderLeft:
                                stepIdx < card.steps.length - 1
                                  ? "1px solid rgba(255,255,255,0.14)"
                                  : "1px solid transparent",
                              pb: stepIdx < card.steps.length - 1 ? 0.5 : 0,
                            }}
                          >
                            <Box
                              aria-hidden
                              sx={{
                                position: "absolute",
                                left: -5,
                                top: 7,
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                bgcolor: "rgba(13, 13, 15, 0.85)",
                                border: "1px solid rgba(255,255,255,0.35)",
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: pageFont,
                                fontWeight: 700,
                                fontSize: "0.9375rem",
                                color: "#fff",
                                mb: 0.75,
                                lineHeight: 1.35,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: "rgba(255,255,255,0.55)",
                                  mr: { sm: 1 },
                                  display: { xs: "block", sm: "inline" },
                                  mb: { xs: 0.25, sm: 0 },
                                }}
                              >
                                {step.time}
                              </Box>
                              {step.title}
                            </Typography>
                            {step.details.map((detail) => (
                              <Typography
                                key={detail}
                                sx={{
                                  fontFamily: pageFont,
                                  fontSize: "0.8125rem",
                                  lineHeight: 1.5,
                                  color: "rgba(255,255,255,0.58)",
                                  mb: 0.25,
                                }}
                              >
                                {detail}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageSection>

      <PageSection ariaLabelledBy="hr-rest-easy-heading">
        <Container maxWidth="lg" sx={{ width: "100%", boxSizing: "border-box" }}>
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
            <Grid item xs={12} md={6} sx={gridCardItemSx}>
              <ScrollReveal variant="fadeLeft" style={gridCardRevealStyle}>
                <Box
                  sx={{
                    ...gridCardSx,
                    justifyContent: "center",
                    gap: { xs: 2, md: 2.5 },
                    py: { xs: 0.5, md: 1 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ShieldOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.55)" }} />
                    <Typography sx={{ ...sectionEyebrowSx, mb: 0 }}>{HR_SECTION.eyebrow}</Typography>
                  </Box>
                  <Typography id="hr-rest-easy-heading" component="h2" sx={{ ...sectionHeadingSx, mb: 0 }}>
                    {HR_SECTION.heading}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontSize: { xs: "0.9375rem", md: "1rem" },
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.78)",
                      maxWidth: 520,
                    }}
                  >
                    {HR_SECTION.body}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontSize: "0.8125rem",
                      lineHeight: 1.5,
                      color: "rgba(255,255,255,0.42)",
                      fontStyle: "italic",
                    }}
                  >
                    {HR_SECTION.disclaimer}
                  </Typography>
                  <Box
                    sx={{
                      mt: { xs: 0.5, md: 1 },
                      p: { xs: 1.75, md: 2 },
                      borderRadius: "10px",
                      border: "1px solid rgba(255,255,255,0.14)",
                      backgroundColor: "rgba(13, 13, 15, 0.45)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: pageFont,
                        fontWeight: 700,
                        fontSize: { xs: "0.75rem", md: "0.8125rem" },
                        letterSpacing: { xs: "0.04em", md: "0.06em" },
                        textTransform: "uppercase",
                        color: "#fff",
                        lineHeight: 1.45,
                      }}
                    >
                      {HR_SECTION.callout}
                    </Typography>
                  </Box>
                </Box>
              </ScrollReveal>
            </Grid>
            <Grid item xs={12} md={6} sx={gridCardItemSx}>
              <ScrollReveal variant="fadeRight" delay={0.1} style={gridCardRevealStyle}>
                <Box sx={{ ...gridCardSx, ...marcelloPanelSx, p: { xs: 2.5, md: 3.25 } }}>
                  <Typography sx={{ ...sectionEyebrowSx, mb: { xs: 2, md: 2.5 } }}>
                    Standard no-go topics
                  </Typography>
                  <Box
                    component="ul"
                    aria-label="Standard no-go topics for corporate comedy"
                    sx={{
                      listStyle: "none",
                      m: 0,
                      p: 0,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      columnGap: { sm: 2.5 },
                      rowGap: { xs: 1.5, md: 1.75 },
                    }}
                  >
                    {NO_GO_TOPICS.map((topic) => (
                      <Box
                        component="li"
                        key={topic}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 1.1 }}
                      >
                        <CloseIcon
                          aria-hidden
                          sx={{ color: "#ef4444", fontSize: 18, mt: "2px", flexShrink: 0 }}
                        />
                        <Typography
                          sx={{
                            fontFamily: pageFont,
                            fontSize: { xs: "0.875rem", md: "0.9375rem" },
                            lineHeight: 1.45,
                            color: "rgba(255,255,255,0.82)",
                          }}
                        >
                          {topic}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </ScrollReveal>
            </Grid>
          </Grid>
        </Container>
      </PageSection>

      <PageSection ariaLabelledBy="optional-add-ons-heading">
        <Container maxWidth="lg" sx={{ width: "100%", boxSizing: "border-box" }}>
          <ScrollReveal variant="fadeUp">
            <Typography id="optional-add-ons-heading" component="h2" sx={{ ...sectionHeadingSx, mb: { xs: 2.5, md: 3 } }}>
              Optional add-ons
            </Typography>
          </ScrollReveal>
          <Grid container spacing={2.5} alignItems="stretch">
            {OPTIONAL_ADD_ONS.map((addon, i) => (
              <Grid item xs={12} sm={6} lg={4} key={addon.title} sx={gridCardItemSx}>
                <ScrollReveal variant="fadeUp" delay={i * 0.08} style={gridCardRevealStyle}>
                  <Box
                    sx={{
                      ...gridCardSx,
                      ...marcelloPanelSx,
                      p: { xs: 2.5, md: 2.75 },
                      minHeight: { md: 196 },
                      gap: 1.5,
                    }}
                  >
                    <Box sx={panelIconSx}>{addon.icon}</Box>
                    <Typography
                      component="h3"
                      sx={{
                        fontFamily: pageFont,
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#fff",
                        lineHeight: 1.35,
                        minHeight: 44,
                      }}
                    >
                      {addon.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: pageFont,
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        color: "rgba(255,255,255,0.65)",
                      }}
                    >
                      {addon.copy}
                    </Typography>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageSection>

      <PageSection id={INQUIRY_SECTION_ID} ariaLabel="Private event inquiry form" sx={{ scrollMarginTop: { xs: 80, md: 96 } }}>
          <Container maxWidth="lg" sx={{ width: "100%", boxSizing: "border-box" }}>
            <ScrollReveal variant="fadeUp">
              <Typography sx={sectionEyebrowSx}>Inquiry</Typography>
              <Typography
                component="h2"
                sx={{
                  ...sectionHeadingSx,
                  mb: { xs: 2, md: 3 },
                }}
              >
                Let&apos;s Build Your Event
              </Typography>
              <Typography
                sx={{
                  fontFamily: pageFont,
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: "rgba(255,255,255,0.78)",
                  maxWidth: 640,
                  mb: { xs: 2, md: 2.5 },
                }}
              >
                Share your headcount, budget, and ideal date. We will follow up with venue options, talent ideas, and a clear plan.
              </Typography>
            </ScrollReveal>

            <ScrollReveal variant="fadeUp" delay={0.1}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              {successMessage && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                    color: "#fff",
                  }}
                >
                  <CheckIcon sx={{ color: "#4ade80", fontSize: 24 }} />
                  <Typography sx={{ fontFamily: pageFont, fontSize: "1rem", color: "#fff" }}>
                    {successMessage}
                  </Typography>
                </Box>
              )}

              {!displayform && (
                <Box
                  sx={{
                    ...marcelloPanelSx,
                    p: { xs: 2.5, sm: 3, md: 4 },
                    maxWidth: "100%",
                    width: "100%",
                    boxSizing: "border-box",
                    overflowX: "hidden",
                  }}
                >
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          component="label"
                          htmlFor="private-event-name"
                          sx={{
                            display: "block",
                            fontFamily: pageFont,
                            fontSize: "0.875rem",
                            color: "#fff",
                            mb: 0.75,
                          }}
                        >
                          Name
                        </Typography>
                        <TextField
                          id="private-event-name"
                          fullWidth
                          placeholder="Your name"
                          variant="outlined"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          component="label"
                          htmlFor="private-event-email"
                          sx={{
                            display: "block",
                            fontFamily: pageFont,
                            fontSize: "0.875rem",
                            color: "#fff",
                            mb: 0.75,
                          }}
                        >
                          Email *
                        </Typography>
                        <TextField
                          id="private-event-email"
                          fullWidth
                          placeholder="you@example.com"
                          variant="outlined"
                          type="email"
                          required
                          value={email}
                          autoComplete="email"
                          onChange={(e) => setEmail(e.target.value)}
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          component="label"
                          htmlFor="private-event-phone"
                          sx={{
                            display: "block",
                            fontFamily: pageFont,
                            fontSize: "0.875rem",
                            color: "#fff",
                            mb: 0.75,
                          }}
                        >
                          Phone number
                        </Typography>
                        <TextField
                          id="private-event-phone"
                          fullWidth
                          placeholder="(555) 555-5555"
                          variant="outlined"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          component="label"
                          htmlFor="private-event-message"
                          sx={{
                            display: "block",
                            fontFamily: pageFont,
                            fontSize: "0.875rem",
                            color: "#fff",
                            mb: 0.75,
                          }}
                        >
                          Message
                        </Typography>
                        <TextField
                          id="private-event-message"
                          fullWidth
                          placeholder="Tell us what's on your mind..."
                          variant="outlined"
                          multiline
                          rows={4}
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          className="cuo-contact-send-message"
                          sx={{
                            fontFamily: pageFont,
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            color: "#000",
                            backgroundColor: "rgba(255,255,255,0.9)",
                            borderRadius: "8px",
                            px: 3,
                            py: 1.25,
                            width: { xs: "100%", sm: "auto" },
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:hover": { backgroundColor: "#fff" },
                            "&:focus-visible": {
                              outline: "2px solid rgba(255,255,255,0.85)",
                              outlineOffset: 2,
                            },
                          }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              )}
            </Box>
            </ScrollReveal>

            <ScrollReveal variant="fadeUp" delay={0.16}>
              <Typography
                component="p"
                sx={{
                  fontFamily: pageFont,
                  fontSize: { xs: "0.8125rem", md: "0.875rem" },
                  lineHeight: 1.75,
                  fontStyle: "italic",
                  color: "rgba(255,255,255,0.58)",
                  maxWidth: 720,
                  mx: "auto",
                  textAlign: "center",
                  mt: { xs: 3, md: 4 },
                }}
              >
                {SEO_FOOTNOTE}
              </Typography>
            </ScrollReveal>
          </Container>
      </PageSection>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          py: { xs: 0.25, md: 0.35 },
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ width: "100%", overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: { xs: 5, md: 8 },
              width: "max-content",
              py: 0,
              animation: "contactPrivateLogosMarquee 75s linear infinite",
              "@keyframes contactPrivateLogosMarquee": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-33.333333333333336%)" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            {[0, 1, 2].flatMap((copy) =>
              PRIVATE_EVENT_LOGOS.map((logo) => {
                const dims = privateEventLogoDims(logo.size, logo.scale ?? 1);
                return (
                  <Box
                    key={`${copy}-${logo.alt}-${logo.src}`}
                    component="img"
                    src={logo.src}
                    alt={logo.alt}
                    loading="lazy"
                    sx={{
                      height: dims.height,
                      maxWidth: dims.maxWidth,
                      width: "auto",
                      objectFit: "contain",
                      opacity: 0.92,
                      flexShrink: 0,
                      ...(logo.nudgeDown && { marginTop: 2 }),
                    }}
                  />
                );
              }),
            )}
          </Box>
        </Box>
      </Box>
      </Box>

      <Footer />
    </>
  );
}
